import {
  Download,
  ImagePlus,
  Plus,
  Share2,
  Trash2,
  UserRoundPlus,
  WandSparkles,
  X,
} from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import crestUrl from './polonia-forged-crest.png';
import { resolveBundledClub } from './leagueClubs';
import { getNextOfficialFixture, officialOpponent } from './officialFixtures';
import { useI18n } from './I18nContext';
import type { Lineup, Match, Player, Team } from './types';

type SocialFormat = 'portrait' | 'story' | 'square';
type MediaMode = 'lineup' | 'final';

type CoachEntry = {
  id: string;
  role: string;
  name: string;
  photo: string;
};

const FORMATS: Record<SocialFormat, { width: number; height: number; label: string }> = {
  portrait: { width: 1080, height: 1350, label: 'Instagram 4:5' },
  story: { width: 1080, height: 1920, label: 'Story 9:16' },
  square: { width: 1080, height: 1080, label: 'Square 1:1' },
};

const MAX_COACHES = 4;

function loadImage(src: string) {
  return new Promise<HTMLImageElement>((resolve, reject) => {
    const image = new Image();
    image.onload = () => resolve(image);
    image.onerror = reject;
    image.src = src;
  });
}

function roundRect(
  context: CanvasRenderingContext2D,
  x: number,
  y: number,
  width: number,
  height: number,
  radius: number,
) {
  const r = Math.min(radius, width / 2, height / 2);
  context.beginPath();
  context.moveTo(x + r, y);
  context.arcTo(x + width, y, x + width, y + height, r);
  context.arcTo(x + width, y + height, x, y + height, r);
  context.arcTo(x, y + height, x, y, r);
  context.arcTo(x, y, x + width, y, r);
  context.closePath();
}

function fitText(
  context: CanvasRenderingContext2D,
  text: string,
  maxWidth: number,
  startSize: number,
  weight = 800,
) {
  let size = startSize;
  while (size > 18) {
    context.font = `${weight} ${size}px -apple-system, BlinkMacSystemFont, "Segoe UI", Arial, sans-serif`;
    if (context.measureText(text).width <= maxWidth) break;
    size -= 2;
  }
  return size;
}

function formatMatchDate(date: string) {
  const parsed = new Date(date);
  if (Number.isNaN(parsed.getTime())) return date;
  return parsed.toLocaleDateString(undefined, {
    weekday: 'short',
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  }).toUpperCase();
}

function sortLineupPlayers(lineup: Lineup, players: Player[]) {
  const slots = (lineup.slots || {}) as Record<string, string | null>;
  const positionOrder = [
    'GK',
    'RB', 'RCB', 'CB', 'LCB', 'LB',
    'RWB', 'RDM', 'CDM', 'LDM', 'LWB',
    'RM', 'RCM', 'CM', 'LCM', 'LM',
    'RW', 'RAM', 'CAM', 'LAM', 'LW',
    'RST', 'ST', 'LST',
  ];

  return Object.entries(slots)
    .filter(([, playerId]) => Boolean(playerId))
    .map(([slot, playerId]) => ({
      slot,
      player: players.find((candidate) => candidate.id === playerId) || null,
    }))
    .filter((entry): entry is { slot: string; player: Player } => Boolean(entry.player))
    .sort((a, b) => {
      const ai = positionOrder.indexOf(a.slot);
      const bi = positionOrder.indexOf(b.slot);
      return (ai === -1 ? 999 : ai) - (bi === -1 ? 999 : bi);
    });
}

async function fileToOptimizedDataUrl(file: File, maxDimension = 1800) {
  const source = await new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result || ''));
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

  const image = await loadImage(source);
  const scale = Math.min(1, maxDimension / Math.max(image.naturalWidth, image.naturalHeight));
  const width = Math.max(1, Math.round(image.naturalWidth * scale));
  const height = Math.max(1, Math.round(image.naturalHeight * scale));

  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const context = canvas.getContext('2d');
  if (!context) return source;
  context.drawImage(image, 0, 0, width, height);
  return canvas.toDataURL('image/jpeg', .88);
}

function drawCoverImage(
  context: CanvasRenderingContext2D,
  image: HTMLImageElement,
  x: number,
  y: number,
  width: number,
  height: number,
) {
  const scale = Math.max(width / image.naturalWidth, height / image.naturalHeight);
  const drawWidth = image.naturalWidth * scale;
  const drawHeight = image.naturalHeight * scale;
  const dx = x + (width - drawWidth) / 2;
  const dy = y + (height - drawHeight) / 2;
  context.save();
  roundRect(context, x, y, width, height, 18);
  context.clip();
  context.drawImage(image, dx, dy, drawWidth, drawHeight);
  context.restore();
}

export function MatchdayMediaStudio({
  team,
  players,
  matches,
  lineups,
  currentFormation,
  currentSlots,
}: {
  team: Team | null;
  players: Player[];
  matches: Match[];
  lineups: Lineup[];
  currentFormation: string;
  currentSlots: Record<string, string | null>;
}) {
  const { t } = useI18n();
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const [format, setFormat] = useState<SocialFormat>('portrait');
  const [mediaMode, setMediaMode] = useState<MediaMode>('lineup');
  const [selectedMatchId, setSelectedMatchId] = useState('');
  const [homeScore, setHomeScore] = useState(0);
  const [awayScore, setAwayScore] = useState(0);
  const [scorersText, setScorersText] = useState('');
  const [assistsText, setAssistsText] = useState('');
  const [playerOfMatchId, setPlayerOfMatchId] = useState('');
  const [selectedLineupId, setSelectedLineupId] = useState('current');
  const [status, setStatus] = useState('');
  const [teamPhoto, setTeamPhoto] = useState('');
  const [substituteIds, setSubstituteIds] = useState<string[]>([]);
  const [coaches, setCoaches] = useState<CoachEntry[]>([
    { id: 'head-coach-dominik', role: t.headCoach, name: 'Dominik Lichon', photo: '' },
    { id: 'head-coach-marcin', role: t.headCoach, name: 'Marcin Śliwa', photo: '' },
  ]);

  const futureMatches = useMemo(
    () =>
      [...matches]
        .filter((match) => new Date(match.match_date).getTime() >= Date.now() - 86400000)
        .sort((a, b) => a.match_date.localeCompare(b.match_date)),
    [matches],
  );

  const completedMatches = useMemo(
    () => [...matches]
      .filter((match) => match.status === 'full_time' || (match.goals_for !== null && match.goals_against !== null))
      .sort((a,b) => b.match_date.localeCompare(a.match_date)),
    [matches],
  );

  const selectableMatches = mediaMode === 'final' ? completedMatches : futureMatches;

  const officialFixture = getNextOfficialFixture();

  const currentLineup: Lineup = {
    id: 'current',
    team_id: team?.id || '',
    name: t.currentLineup,
    formation: currentFormation,
    slots: currentSlots,
  };

  const lineupOptions = [currentLineup, ...lineups];

  const selectedLineup =
    lineupOptions.find((lineup) => lineup.id === selectedLineupId) || currentLineup;

  const selectedDbMatch =
    selectableMatches.find((match) => match.id === selectedMatchId) || selectableMatches[0] || null;

  const matchInfo = selectedDbMatch
    ? {
        id: selectedDbMatch.id,
        opponent: selectedDbMatch.opponent || t.opponent,
        date: selectedDbMatch.match_date,
        competition: selectedDbMatch.competition || 'GDSFC',
        round: '',
      }
    : officialFixture
      ? {
          id: `official-${officialFixture.round}`,
          opponent: officialOpponent(officialFixture),
          date: `${officialFixture.date}T12:00:00`,
          competition: officialFixture.competition,
          round: `${t.round} ${officialFixture.round}`,
        }
      : null;

  const lineupPlayers = useMemo(
    () => sortLineupPlayers(selectedLineup, players),
    [selectedLineup, players],
  );

  const starterIds = useMemo(
    () => new Set(lineupPlayers.map((entry) => entry.player.id)),
    [lineupPlayers],
  );

  const benchCandidates = useMemo(
    () =>
      [...players]
        .filter((player) => !starterIds.has(player.id))
        .sort((a, b) => (a.squad_number ?? 999) - (b.squad_number ?? 999)),
    [players, starterIds],
  );

  const selectedSubstitutes = useMemo(
    () =>
      substituteIds
        .map((id) => players.find((player) => player.id === id))
        .filter((player): player is Player => Boolean(player)),
    [substituteIds, players],
  );

  const coachStorageKey = `ecc_media_coaches_${team?.id || 'default'}`;
  const matchStorageKey = `ecc_match_media_${matchInfo?.id || 'none'}`;

  useEffect(() => {
    try {
      const saved = localStorage.getItem(coachStorageKey);
      if (!saved) return;
      const parsed = JSON.parse(saved) as CoachEntry[];
      if (Array.isArray(parsed) && parsed.length) setCoaches(parsed.slice(0, MAX_COACHES));
    } catch {
      // Local device fallback only.
    }
  }, [coachStorageKey]);

  useEffect(() => {
    try {
      localStorage.setItem(coachStorageKey, JSON.stringify(coaches));
    } catch {
      // Ignore private browsing/quota restrictions.
    }
  }, [coachStorageKey, coaches]);

  useEffect(() => {
    try {
      const saved = localStorage.getItem(matchStorageKey);
      if (!saved) {
        setTeamPhoto('');
        setSubstituteIds([]);
        setHomeScore(Number(selectedDbMatch?.goals_for ?? 0));
        setAwayScore(Number(selectedDbMatch?.goals_against ?? 0));

        const contributions = selectedDbMatch?.contributions || [];
        const scorerLines = contributions
          .filter((entry) => Number(entry.goals || 0) > 0)
          .map((entry) => {
            const player = players.find((candidate) => candidate.id === entry.player_id);
            const goals = Number(entry.goals || 0);
            return player ? `${player.name}${goals > 1 ? ` ×${goals}` : ''}` : '';
          })
          .filter(Boolean);
        const assistLines = contributions
          .filter((entry) => Number(entry.assists || 0) > 0)
          .map((entry) => {
            const player = players.find((candidate) => candidate.id === entry.player_id);
            const assists = Number(entry.assists || 0);
            return player ? `${player.name}${assists > 1 ? ` ×${assists}` : ''}` : '';
          })
          .filter(Boolean);

        setScorersText(scorerLines.join('\n'));
        setAssistsText(assistLines.join('\n'));
        setPlayerOfMatchId(selectedDbMatch?.mvp_player_id || '');
        return;
      }
      const parsed = JSON.parse(saved) as {
        teamPhoto?: string;
        substituteIds?: string[];
        mediaMode?: MediaMode;
        homeScore?: number;
        awayScore?: number;
        scorersText?: string;
        assistsText?: string;
        playerOfMatchId?: string;
      };
      setTeamPhoto(parsed.teamPhoto || '');
      setSubstituteIds(Array.isArray(parsed.substituteIds) ? parsed.substituteIds : []);
      setHomeScore(Number.isFinite(parsed.homeScore) ? Number(parsed.homeScore) : Number(selectedDbMatch?.goals_for ?? 0));
      setAwayScore(Number.isFinite(parsed.awayScore) ? Number(parsed.awayScore) : Number(selectedDbMatch?.goals_against ?? 0));
      setScorersText(parsed.scorersText || '');
      setAssistsText(parsed.assistsText || '');
      setPlayerOfMatchId(selectedDbMatch?.mvp_player_id || parsed.playerOfMatchId || '');
    } catch {
      setTeamPhoto('');
      setSubstituteIds([]);
      setHomeScore(Number(selectedDbMatch?.goals_for ?? 0));
      setAwayScore(Number(selectedDbMatch?.goals_against ?? 0));
      setScorersText('');
      setAssistsText('');
      setPlayerOfMatchId('');
    }
  }, [matchStorageKey, selectedDbMatch]);

  useEffect(() => {
    if (!matchInfo) return;
    try {
      localStorage.setItem(matchStorageKey, JSON.stringify({
        teamPhoto,
        substituteIds,
        mediaMode,
        homeScore,
        awayScore,
        scorersText,
        assistsText,
        playerOfMatchId,
      }));
    } catch {
      setStatus(t.photoStorageWarning);
    }
  }, [
    matchInfo,
    matchStorageKey,
    substituteIds,
    teamPhoto,
    mediaMode,
    homeScore,
    awayScore,
    scorersText,
    assistsText,
    playerOfMatchId,
    t.photoStorageWarning,
  ]);

  function toggleSubstitute(playerId: string) {
    setSubstituteIds((current) =>
      current.includes(playerId)
        ? current.filter((id) => id !== playerId)
        : [...current, playerId],
    );
  }

  function updateCoach(id: string, field: keyof CoachEntry, value: string) {
    setCoaches((current) =>
      current.map((coach) => (coach.id === id ? { ...coach, [field]: value } : coach)),
    );
  }

  function addCoach() {
    if (coaches.length >= MAX_COACHES) return;
    setCoaches((current) => [
      ...current,
      {
        id: crypto.randomUUID(),
        role: t.coach,
        name: '',
        photo: '',
      },
    ]);
  }

  function removeCoach(id: string) {
    setCoaches((current) => current.filter((coach) => coach.id !== id));
  }

  async function handleTeamPhoto(file?: File) {
    if (!file) return;
    try {
      setStatus(t.optimizingPhoto);
      setTeamPhoto(await fileToOptimizedDataUrl(file, 1800));
      setStatus(t.photoReady);
    } catch {
      setStatus(t.photoUploadFailed);
    }
  }

  async function handleCoachPhoto(id: string, file?: File) {
    if (!file) return;
    try {
      const photo = await fileToOptimizedDataUrl(file, 800);
      updateCoach(id, 'photo', photo);
    } catch {
      setStatus(t.photoUploadFailed);
    }
  }

  async function drawGraphic() {
    const canvas = canvasRef.current;
    if (!canvas || !matchInfo) return null;

    const spec = FORMATS[format];
    canvas.width = spec.width;
    canvas.height = spec.height;

    const ctx = canvas.getContext('2d');
    if (!ctx) return null;

    const w = spec.width;
    const h = spec.height;
    const story = format === 'story';
    const square = format === 'square';

    // Background.
    const bg = ctx.createLinearGradient(0, 0, w, h);
    bg.addColorStop(0, '#031221');
    bg.addColorStop(.38, '#071523');
    bg.addColorStop(.74, '#160711');
    bg.addColorStop(1, '#02070d');
    ctx.fillStyle = bg;
    ctx.fillRect(0, 0, w, h);

    let ambient = ctx.createRadialGradient(w * .14, h * .2, 0, w * .14, h * .2, w * .72);
    ambient.addColorStop(0, 'rgba(9,80,190,.35)');
    ambient.addColorStop(1, 'rgba(9,80,190,0)');
    ctx.fillStyle = ambient;
    ctx.fillRect(0, 0, w, h);

    ambient = ctx.createRadialGradient(w * .9, h * .28, 0, w * .9, h * .28, w * .7);
    ambient.addColorStop(0, 'rgba(185,24,45,.26)');
    ambient.addColorStop(1, 'rgba(185,24,45,0)');
    ctx.fillStyle = ambient;
    ctx.fillRect(0, 0, w, h);

    const border = ctx.createLinearGradient(0, 0, w, 0);
    border.addColorStop(0, '#707982');
    border.addColorStop(.18, '#e6bd45');
    border.addColorStop(.48, '#e8edf0');
    border.addColorStop(.68, '#dcae3b');
    border.addColorStop(1, '#6b202e');
    ctx.strokeStyle = border;
    ctx.lineWidth = 6;
    ctx.strokeRect(22, 22, w - 44, h - 44);

    // Heading.
    ctx.textAlign = 'center';
    ctx.fillStyle = '#e8bf48';
    ctx.font = '900 29px -apple-system, BlinkMacSystemFont, "Segoe UI", Arial, sans-serif';
    ctx.fillText('POLONIA GLASGOW AFC', w / 2, 72);

    ctx.fillStyle = '#ffffff';
    ctx.font = `900 ${story ? 66 : square ? 48 : 58}px -apple-system, BlinkMacSystemFont, "Segoe UI", Arial, sans-serif`;
    ctx.fillText(
      (mediaMode === 'final' ? t.fullTime : t.matchday).toUpperCase(),
      w / 2,
      story ? 146 : 136,
    );

    ctx.fillStyle = '#aebac4';
    ctx.font = '700 23px -apple-system, BlinkMacSystemFont, "Segoe UI", Arial, sans-serif';
    ctx.fillText(
      `${matchInfo.competition.toUpperCase()}${matchInfo.round ? ` · ${matchInfo.round.toUpperCase()}` : ''}`,
      w / 2,
      story ? 188 : 176,
    );

    const opponent = resolveBundledClub(matchInfo.opponent);
    const [homeCrest, opponentCrest, teamPhotoImage] = await Promise.all([
      loadImage(crestUrl).catch(() => null),
      opponent?.logo ? loadImage(opponent.logo).catch(() => null) : Promise.resolve(null),
      teamPhoto ? loadImage(teamPhoto).catch(() => null) : Promise.resolve(null),
    ]);

    if (mediaMode === 'final') {
      const resultCrestY = story ? 245 : 215;
      const resultCrestSize = story ? 230 : square ? 165 : 195;

      if (homeCrest) {
        ctx.save();
        ctx.shadowColor = 'rgba(229,184,64,.42)';
        ctx.shadowBlur = 30;
        ctx.drawImage(homeCrest, 105, resultCrestY, resultCrestSize, resultCrestSize);
        ctx.restore();
      }

      if (opponentCrest) {
        ctx.save();
        ctx.shadowColor = 'rgba(255,255,255,.18)';
        ctx.shadowBlur = 18;
        ctx.drawImage(
          opponentCrest,
          w - 105 - resultCrestSize,
          resultCrestY + 12,
          resultCrestSize,
          resultCrestSize - 24,
        );
        ctx.restore();
      }

      ctx.textAlign = 'center';
      ctx.fillStyle = '#ffffff';
      ctx.font = `950 ${story ? 118 : square ? 90 : 105}px -apple-system, BlinkMacSystemFont, "Segoe UI", Arial, sans-serif`;
      ctx.fillText(`${homeScore}  —  ${awayScore}`, w / 2, resultCrestY + resultCrestSize * .58);

      ctx.font = `900 ${story ? 30 : 25}px -apple-system, BlinkMacSystemFont, "Segoe UI", Arial, sans-serif`;
      ctx.fillText('POLONIA', 105 + resultCrestSize / 2, resultCrestY + resultCrestSize + 34);

      const resultOpponentSize = fitText(ctx, matchInfo.opponent.toUpperCase(), 325, story ? 29 : 25);
      ctx.font = `900 ${resultOpponentSize}px -apple-system, BlinkMacSystemFont, "Segoe UI", Arial, sans-serif`;
      ctx.fillText(
        matchInfo.opponent.toUpperCase(),
        w - 105 - resultCrestSize / 2,
        resultCrestY + resultCrestSize + 34,
      );

      const resultStripY = resultCrestY + resultCrestSize + 72;
      roundRect(ctx, 65, resultStripY, w - 130, 78, 16);
      const resultStrip = ctx.createLinearGradient(65, resultStripY, w - 65, resultStripY);
      resultStrip.addColorStop(0, 'rgba(4,24,43,.95)');
      resultStrip.addColorStop(.5, 'rgba(12,18,27,.97)');
      resultStrip.addColorStop(1, 'rgba(41,8,18,.95)');
      ctx.fillStyle = resultStrip;
      ctx.fill();
      ctx.strokeStyle = 'rgba(222,181,66,.72)';
      ctx.lineWidth = 2;
      ctx.stroke();

      ctx.fillStyle = '#ffffff';
      ctx.font = '800 24px -apple-system, BlinkMacSystemFont, "Segoe UI", Arial, sans-serif';
      ctx.fillText(formatMatchDate(matchInfo.date), w / 2, resultStripY + 31);
      ctx.fillStyle = '#e3bd48';
      ctx.font = '800 18px -apple-system, BlinkMacSystemFont, "Segoe UI", Arial, sans-serif';
      ctx.fillText(matchInfo.competition.toUpperCase(), w / 2, resultStripY + 58);

      let resultContentTop = resultStripY + 100;

      if (teamPhotoImage) {
        const photoHeight = story ? 430 : square ? 250 : 310;
        drawCoverImage(ctx, teamPhotoImage, 65, resultContentTop, w - 130, photoHeight);
        const overlay = ctx.createLinearGradient(0, resultContentTop, 0, resultContentTop + photoHeight);
        overlay.addColorStop(0, 'rgba(2,8,16,.02)');
        overlay.addColorStop(.65, 'rgba(2,8,16,.08)');
        overlay.addColorStop(1, 'rgba(2,8,16,.82)');
        ctx.fillStyle = overlay;
        roundRect(ctx, 65, resultContentTop, w - 130, photoHeight, 18);
        ctx.fill();
        resultContentTop += photoHeight + 18;
      }

      const pom = players.find((player) => player.id === playerOfMatchId) || null;
      const pomImage = pom?.photo_url ? await loadImage(pom.photo_url).catch(() => null) : null;

      const footerReserve = 64;
      const available = h - resultContentTop - footerReserve;
      const gap = 14;
      const leftWidth = Math.round((w - 130 - gap) * .61);
      const rightWidth = w - 130 - gap - leftWidth;
      const panelHeight = Math.max(220, available);

      roundRect(ctx, 65, resultContentTop, leftWidth, panelHeight, 18);
      ctx.fillStyle = 'rgba(2,11,20,.84)';
      ctx.fill();
      ctx.strokeStyle = 'rgba(94,121,139,.7)';
      ctx.lineWidth = 2;
      ctx.stroke();

      ctx.textAlign = 'left';
      ctx.fillStyle = '#e5bd49';
      ctx.font = '900 20px -apple-system, BlinkMacSystemFont, "Segoe UI", Arial, sans-serif';
      ctx.fillText(t.scorers.toUpperCase(), 88, resultContentTop + 35);

      const scorerLines = scorersText.split('\n').map((line) => line.trim()).filter(Boolean);
      ctx.fillStyle = '#ffffff';
      let y = resultContentTop + 70;
      const maxScorerLines = story ? 7 : 5;
      scorerLines.slice(0, maxScorerLines).forEach((line) => {
        const size = fitText(ctx, line.toUpperCase(), leftWidth - 48, story ? 26 : 22);
        ctx.font = `850 ${size}px -apple-system, BlinkMacSystemFont, "Segoe UI", Arial, sans-serif`;
        ctx.fillText(line.toUpperCase(), 88, y);
        y += story ? 42 : 36;
      });
      if (!scorerLines.length) {
        ctx.fillStyle = '#8295a3';
        ctx.font = '800 18px -apple-system, BlinkMacSystemFont, "Segoe UI", Arial, sans-serif';
        ctx.fillText('—', 88, y);
        y += 36;
      }

      ctx.fillStyle = '#e5bd49';
      ctx.font = '900 20px -apple-system, BlinkMacSystemFont, "Segoe UI", Arial, sans-serif';
      ctx.fillText(t.assists.toUpperCase(), 88, y + 12);
      y += 46;

      const assistLines = assistsText.split('\n').map((line) => line.trim()).filter(Boolean);
      ctx.fillStyle = '#ffffff';
      assistLines.slice(0, story ? 6 : 4).forEach((line) => {
        const size = fitText(ctx, line.toUpperCase(), leftWidth - 48, story ? 24 : 20);
        ctx.font = `800 ${size}px -apple-system, BlinkMacSystemFont, "Segoe UI", Arial, sans-serif`;
        ctx.fillText(line.toUpperCase(), 88, y);
        y += story ? 39 : 34;
      });
      if (!assistLines.length) {
        ctx.fillStyle = '#8295a3';
        ctx.font = '800 18px -apple-system, BlinkMacSystemFont, "Segoe UI", Arial, sans-serif';
        ctx.fillText('—', 88, y);
      }

      const rightX = 65 + leftWidth + gap;
      roundRect(ctx, rightX, resultContentTop, rightWidth, panelHeight, 18);
      ctx.fillStyle = 'rgba(2,11,20,.84)';
      ctx.fill();
      ctx.strokeStyle = 'rgba(226,185,70,.66)';
      ctx.lineWidth = 2;
      ctx.stroke();

      ctx.fillStyle = '#e5bd49';
      ctx.font = `900 ${story ? 22 : 19}px -apple-system, BlinkMacSystemFont, "Segoe UI", Arial, sans-serif`;
      ctx.fillText(t.playerOfTheMatch.toUpperCase(), rightX + 20, resultContentTop + 35);

      const portraitSize = Math.min(rightWidth - 40, story ? 250 : 190);
      const portraitX = rightX + (rightWidth - portraitSize) / 2;
      const portraitY = resultContentTop + 58;

      if (pomImage) {
        drawCoverImage(ctx, pomImage, portraitX, portraitY, portraitSize, portraitSize);
      } else {
        roundRect(ctx, portraitX, portraitY, portraitSize, portraitSize, 16);
        const placeholder = ctx.createLinearGradient(portraitX, portraitY, portraitX, portraitY + portraitSize);
        placeholder.addColorStop(0, '#0a2944');
        placeholder.addColorStop(1, '#160812');
        ctx.fillStyle = placeholder;
        ctx.fill();
        ctx.strokeStyle = 'rgba(225,184,67,.48)';
        ctx.stroke();
        ctx.textAlign = 'center';
        ctx.fillStyle = '#d9b84d';
        ctx.font = `950 ${Math.round(portraitSize * .28)}px -apple-system, BlinkMacSystemFont, "Segoe UI", Arial, sans-serif`;
        ctx.fillText('★', rightX + rightWidth / 2, portraitY + portraitSize * .62);
        ctx.textAlign = 'left';
      }

      ctx.textAlign = 'center';
      ctx.fillStyle = '#ffffff';
      const pomName = pom?.name || t.toBeSelected;
      const pomSize = fitText(ctx, pomName.toUpperCase(), rightWidth - 36, story ? 27 : 23);
      ctx.font = `950 ${pomSize}px -apple-system, BlinkMacSystemFont, "Segoe UI", Arial, sans-serif`;
      ctx.fillText(pomName.toUpperCase(), rightX + rightWidth / 2, portraitY + portraitSize + 38);

      if (pom) {
        ctx.fillStyle = '#9badb9';
        ctx.font = '800 16px -apple-system, BlinkMacSystemFont, "Segoe UI", Arial, sans-serif';
        ctx.fillText(
          `#${pom.squad_number ?? '—'} · ${pom.position || '—'}`,
          rightX + rightWidth / 2,
          portraitY + portraitSize + 64,
        );
      }

      ctx.textAlign = 'center';
      ctx.fillStyle = '#7f919e';
      ctx.font = '700 16px -apple-system, BlinkMacSystemFont, "Segoe UI", Arial, sans-serif';
      ctx.fillText('ELITE COACH CLOUD · FULL TIME', w / 2, h - 24);

      return canvas;
    }

    const crestY = story ? 225 : 205;
    const crestSize = story ? 250 : square ? 175 : 205;

    if (homeCrest) {
      ctx.save();
      ctx.shadowColor = 'rgba(229,184,64,.38)';
      ctx.shadowBlur = 28;
      ctx.drawImage(homeCrest, 118, crestY, crestSize, crestSize);
      ctx.restore();
    }

    if (opponentCrest) {
      ctx.save();
      ctx.shadowColor = 'rgba(255,255,255,.18)';
      ctx.shadowBlur = 18;
      ctx.drawImage(
        opponentCrest,
        w - 118 - crestSize,
        crestY + 15,
        crestSize,
        crestSize - 30,
      );
      ctx.restore();
    }

    ctx.fillStyle = '#ffffff';
    ctx.font = `900 ${story ? 56 : 48}px -apple-system, BlinkMacSystemFont, "Segoe UI", Arial, sans-serif`;
    ctx.fillText('VS', w / 2, crestY + crestSize * .52);

    ctx.font = '900 27px -apple-system, BlinkMacSystemFont, "Segoe UI", Arial, sans-serif';
    ctx.fillText('POLONIA', 118 + crestSize / 2, crestY + crestSize + 35);

    const opponentNameSize = fitText(ctx, matchInfo.opponent.toUpperCase(), 325, 27);
    ctx.font = `900 ${opponentNameSize}px -apple-system, BlinkMacSystemFont, "Segoe UI", Arial, sans-serif`;
    ctx.fillText(
      matchInfo.opponent.toUpperCase(),
      w - 118 - crestSize / 2,
      crestY + crestSize + 35,
    );

    // Match strip.
    const stripY = crestY + crestSize + 70;
    roundRect(ctx, 65, stripY, w - 130, 82, 17);
    const strip = ctx.createLinearGradient(65, stripY, w - 65, stripY);
    strip.addColorStop(0, 'rgba(4,24,43,.94)');
    strip.addColorStop(.5, 'rgba(12,18,27,.96)');
    strip.addColorStop(1, 'rgba(41,8,18,.94)');
    ctx.fillStyle = strip;
    ctx.fill();
    ctx.strokeStyle = 'rgba(222,181,66,.7)';
    ctx.lineWidth = 2;
    ctx.stroke();

    ctx.fillStyle = '#ffffff';
    ctx.font = '800 25px -apple-system, BlinkMacSystemFont, "Segoe UI", Arial, sans-serif';
    ctx.fillText(formatMatchDate(matchInfo.date), w / 2, stripY + 34);
    ctx.fillStyle = '#e3bd48';
    ctx.font = '800 19px -apple-system, BlinkMacSystemFont, "Segoe UI", Arial, sans-serif';
    ctx.fillText(
      `${selectedLineup.formation || currentFormation} · ${lineupPlayers.length}/11`,
      w / 2,
      stripY + 62,
    );

    // For portrait/story, use a real photo banner if supplied.
    let contentTop = stripY + 104;
    let teamPhotoBottom = contentTop;
    if (teamPhotoImage && !square) {
      const photoHeight = story ? 350 : 255;
      drawCoverImage(ctx, teamPhotoImage, 65, contentTop, w - 130, photoHeight);

      const overlay = ctx.createLinearGradient(0, contentTop, 0, contentTop + photoHeight);
      overlay.addColorStop(0, 'rgba(2,8,16,.04)');
      overlay.addColorStop(.7, 'rgba(2,8,16,.1)');
      overlay.addColorStop(1, 'rgba(2,8,16,.78)');
      ctx.fillStyle = overlay;
      roundRect(ctx, 65, contentTop, w - 130, photoHeight, 18);
      ctx.fill();

      ctx.textAlign = 'left';
      ctx.fillStyle = '#e5bd49';
      ctx.font = '900 21px -apple-system, BlinkMacSystemFont, "Segoe UI", Arial, sans-serif';
      ctx.fillText(t.teamPhoto.toUpperCase(), 88, contentTop + 33);

      teamPhotoBottom = contentTop + photoHeight + 24;
      contentTop = teamPhotoBottom;
    }

    // Main lineup + substitutes.
    const footerHeight = coaches.some((coach) => coach.name.trim()) ? (story ? 185 : 145) : 74;
    const panelBottom = h - footerHeight - 45;
    const panelHeight = Math.max(280, panelBottom - contentTop);
    const panelGap = 14;
    const startingWidth = selectedSubstitutes.length ? Math.round((w - 130 - panelGap) * .67) : w - 130;
    const benchWidth = selectedSubstitutes.length ? w - 130 - panelGap - startingWidth : 0;

    roundRect(ctx, 65, contentTop, startingWidth, panelHeight, 19);
    ctx.fillStyle = 'rgba(2,11,20,.8)';
    ctx.fill();
    ctx.strokeStyle = 'rgba(91,118,137,.72)';
    ctx.lineWidth = 2;
    ctx.stroke();

    ctx.textAlign = 'left';
    ctx.fillStyle = '#e5bd49';
    ctx.font = '900 23px -apple-system, BlinkMacSystemFont, "Segoe UI", Arial, sans-serif';
    ctx.fillText(t.startingXI.toUpperCase(), 88, contentTop + 39);

    const xiColumns = story ? 1 : 2;
    const xiLeft = 88;
    const xiTop = contentTop + 57;
    const xiAvailable = startingWidth - 46;
    const xiGap = 12;
    const xiColWidth = (xiAvailable - xiGap * (xiColumns - 1)) / xiColumns;
    const xiRows = Math.ceil(Math.max(11, lineupPlayers.length) / xiColumns);
    const xiRowHeight = Math.min(
      story ? 76 : 64,
      (panelHeight - 76) / Math.max(1, xiRows),
    );

    for (let i = 0; i < Math.max(11, lineupPlayers.length); i += 1) {
      const col = i % xiColumns;
      const row = Math.floor(i / xiColumns);
      const x = xiLeft + col * (xiColWidth + xiGap);
      const y = xiTop + row * xiRowHeight;
      const entry = lineupPlayers[i];

      roundRect(ctx, x, y, xiColWidth, xiRowHeight - 6, 11);
      const cardGradient = ctx.createLinearGradient(x, y, x + xiColWidth, y);
      cardGradient.addColorStop(0, 'rgba(7,40,69,.88)');
      cardGradient.addColorStop(.72, 'rgba(4,14,24,.92)');
      cardGradient.addColorStop(1, 'rgba(48,8,19,.8)');
      ctx.fillStyle = cardGradient;
      ctx.fill();
      ctx.strokeStyle = entry ? 'rgba(226,185,70,.58)' : 'rgba(74,95,111,.35)';
      ctx.lineWidth = 1.3;
      ctx.stroke();

      if (entry) {
        ctx.fillStyle = '#e4bd49';
        ctx.font = `900 ${story ? 25 : 22}px -apple-system, BlinkMacSystemFont, "Segoe UI", Arial, sans-serif`;
        ctx.fillText(String(entry.player.squad_number ?? '—'), x + 13, y + xiRowHeight * .56);

        ctx.fillStyle = '#8fa2b0';
        ctx.font = `800 ${story ? 15 : 13}px -apple-system, BlinkMacSystemFont, "Segoe UI", Arial, sans-serif`;
        ctx.fillText(entry.slot, x + 58, y + xiRowHeight * .55);

        const maxName = xiColWidth - 122;
        const nameSize = fitText(ctx, entry.player.name.toUpperCase(), maxName, story ? 23 : 20);
        ctx.fillStyle = '#fff';
        ctx.font = `900 ${nameSize}px -apple-system, BlinkMacSystemFont, "Segoe UI", Arial, sans-serif`;
        ctx.fillText(entry.player.name.toUpperCase(), x + 94, y + xiRowHeight * .56);
      }
    }

    // Bench panel.
    if (selectedSubstitutes.length) {
      const benchX = 65 + startingWidth + panelGap;
      roundRect(ctx, benchX, contentTop, benchWidth, panelHeight, 19);
      ctx.fillStyle = 'rgba(2,11,20,.8)';
      ctx.fill();
      ctx.strokeStyle = 'rgba(226,185,70,.62)';
      ctx.lineWidth = 2;
      ctx.stroke();

      ctx.fillStyle = '#e5bd49';
      ctx.font = '900 21px -apple-system, BlinkMacSystemFont, "Segoe UI", Arial, sans-serif';
      ctx.fillText(t.substitutes.toUpperCase(), benchX + 18, contentTop + 39);

      const availableBenchHeight = panelHeight - 66;
      const benchRowHeight = Math.min(
        54,
        availableBenchHeight / Math.max(1, selectedSubstitutes.length),
      );

      selectedSubstitutes.forEach((player, index) => {
        const y = contentTop + 58 + index * benchRowHeight;
        ctx.fillStyle = '#e4bd49';
        ctx.font = '900 19px -apple-system, BlinkMacSystemFont, "Segoe UI", Arial, sans-serif';
        ctx.fillText(String(player.squad_number ?? '—'), benchX + 16, y + benchRowHeight * .58);

        const nameSize = fitText(ctx, player.name.toUpperCase(), benchWidth - 72, 18);
        ctx.fillStyle = '#fff';
        ctx.font = `800 ${nameSize}px -apple-system, BlinkMacSystemFont, "Segoe UI", Arial, sans-serif`;
        ctx.fillText(player.name.toUpperCase(), benchX + 52, y + benchRowHeight * .58);
      });
    }

    // Coaches footer.
    const activeCoaches = coaches.filter((coach) => coach.name.trim());
    if (activeCoaches.length) {
      const coachTop = h - footerHeight + 4;
      roundRect(ctx, 65, coachTop, w - 130, footerHeight - 54, 16);
      ctx.fillStyle = 'rgba(3,12,21,.92)';
      ctx.fill();
      ctx.strokeStyle = 'rgba(224,183,67,.58)';
      ctx.lineWidth = 2;
      ctx.stroke();

      const coachWidth = (w - 160) / activeCoaches.length;
      const coachImages = await Promise.all(
        activeCoaches.map((coach) =>
          coach.photo ? loadImage(coach.photo).catch(() => null) : Promise.resolve(null),
        ),
      );

      activeCoaches.forEach((coach, index) => {
        const x = 80 + index * coachWidth;
        const photo = coachImages[index];
        const photoSize = footerHeight > 160 ? 76 : 58;

        if (photo) {
          ctx.save();
          ctx.beginPath();
          ctx.arc(x + photoSize / 2, coachTop + (footerHeight - 54) / 2, photoSize / 2, 0, Math.PI * 2);
          ctx.clip();
          ctx.drawImage(
            photo,
            x,
            coachTop + (footerHeight - 54 - photoSize) / 2,
            photoSize,
            photoSize,
          );
          ctx.restore();
        } else {
          ctx.beginPath();
          ctx.arc(
            x + photoSize / 2,
            coachTop + (footerHeight - 54) / 2,
            photoSize / 2,
            0,
            Math.PI * 2,
          );
          ctx.fillStyle = '#0a1b29';
          ctx.fill();
          ctx.strokeStyle = '#d8b447';
          ctx.stroke();
        }

        const textX = x + photoSize + 12;
        ctx.fillStyle = '#dcb84b';
        ctx.font = '800 14px -apple-system, BlinkMacSystemFont, "Segoe UI", Arial, sans-serif';
        ctx.fillText(coach.role.toUpperCase(), textX, coachTop + 31);

        const nameSize = fitText(ctx, coach.name.toUpperCase(), coachWidth - photoSize - 22, 19);
        ctx.fillStyle = '#fff';
        ctx.font = `900 ${nameSize}px -apple-system, BlinkMacSystemFont, "Segoe UI", Arial, sans-serif`;
        ctx.fillText(coach.name.toUpperCase(), textX, coachTop + 57);
      });
    }

    ctx.textAlign = 'center';
    ctx.fillStyle = '#7f919e';
    ctx.font = '700 16px -apple-system, BlinkMacSystemFont, "Segoe UI", Arial, sans-serif';
    ctx.fillText('ELITE COACH CLOUD · MATCHDAY INTELLIGENCE', w / 2, h - 24);

    return canvas;
  }

  async function getBlob() {
    setStatus(t.generatingGraphic);
    const canvas = await drawGraphic();
    if (!canvas) {
      setStatus(t.noMatchAvailable);
      return null;
    }
    return new Promise<Blob | null>((resolve) => {
      canvas.toBlob((blob) => resolve(blob), 'image/png', 1);
    });
  }

  async function download() {
    const blob = await getBlob();
    if (!blob) return;
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `polonia-${mediaMode === 'final' ? 'full-time' : 'matchday'}-${format}.png`;
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.setTimeout(() => URL.revokeObjectURL(url), 1000);
    setStatus(t.graphicReady);
  }

  async function share() {
    const blob = await getBlob();
    if (!blob) return;

    const file = new File([blob], `polonia-${mediaMode === 'final' ? 'full-time' : 'matchday'}-${format}.png`, { type: 'image/png' });
    const shareData = {
      files: [file],
      title: `Polonia Glasgow AFC · ${mediaMode === 'final' ? t.fullTime : t.matchday}`,
      text: `${matchInfo?.opponent || ''} · ${formatMatchDate(matchInfo?.date || '')}`,
    };

    try {
      if (navigator.share && (!navigator.canShare || navigator.canShare(shareData))) {
        await navigator.share(shareData);
        setStatus(t.sharedSuccessfully);
        return;
      }
    } catch (error) {
      if ((error as Error)?.name === 'AbortError') {
        setStatus('');
        return;
      }
    }

    await download();
  }

  return (
    <section className="metal-card matchday-media-studio">
      <div className="section-title-line">
        <div>
          <p className="eyebrow">MATCHDAY MEDIA STUDIO</p>
          <h2>{t.matchdayGraphic}</h2>
          <p className="muted">{t.matchdayGraphicSubtitle}</p>
        </div>
        <WandSparkles size={26} />
      </div>

      <div className="media-mode-switch">
        <button
          type="button"
          className={mediaMode === 'lineup' ? 'active' : ''}
          onClick={() => setMediaMode('lineup')}
        >
          {t.lineupGraphic}
        </button>
        <button
          type="button"
          className={mediaMode === 'final' ? 'active' : ''}
          onClick={() => setMediaMode('final')}
        >
          {t.fullTimeGraphic}
        </button>
      </div>

      <div className="media-studio-controls">
        <label>
          <span>{t.matchday}</span>
          <select
            value={selectedMatchId}
            onChange={(event) => setSelectedMatchId(event.target.value)}
          >
            {!selectableMatches.length && mediaMode === 'lineup' && officialFixture && (
              <option value="">
                {formatMatchDate(`${officialFixture.date}T12:00:00`)} · {officialOpponent(officialFixture)}
              </option>
            )}
            {selectableMatches.map((match) => (
              <option key={match.id} value={match.id}>
                {formatMatchDate(match.match_date)} · {match.opponent || t.opponent}
              </option>
            ))}
          </select>
        </label>

        <label>
          <span>{t.lineup}</span>
          <select
            value={selectedLineupId}
            onChange={(event) => setSelectedLineupId(event.target.value)}
          >
            {lineupOptions.map((lineup) => (
              <option key={lineup.id} value={lineup.id}>
                {lineup.name} · {lineup.formation || '—'}
              </option>
            ))}
          </select>
        </label>

        <label>
          <span>{t.socialFormat}</span>
          <select value={format} onChange={(event) => setFormat(event.target.value as SocialFormat)}>
            {Object.entries(FORMATS).map(([key, value]) => (
              <option key={key} value={key}>{value.label}</option>
            ))}
          </select>
        </label>
      </div>

      <div className="media-studio-summary">
        <div>
          <span>{t.selectedPlayers}</span>
          <strong className={lineupPlayers.length === 11 ? 'complete' : ''}>{lineupPlayers.length}/11</strong>
        </div>
        <div>
          <span>{t.substitutes}</span>
          <strong>{selectedSubstitutes.length}</strong>
        </div>
        <div>
          <span>{t.opponent}</span>
          <strong>{matchInfo?.opponent || '—'}</strong>
        </div>
        <div>
          <span>{t.formation}</span>
          <strong>{selectedLineup.formation || currentFormation}</strong>
        </div>
      </div>

      {mediaMode === 'final' && (
        <section className="media-editor-section final-score-editor">
          <div className="media-editor-heading">
            <div>
              <WandSparkles size={19} />
              <strong>{t.fullTimeGraphic}</strong>
            </div>
          </div>

          <div className="final-score-inputs">
            <label>
              <span>Polonia</span>
              <input
                type="number"
                min="0"
                max="99"
                value={homeScore}
                onFocus={(event) => { if (event.currentTarget.value === '0') event.currentTarget.value = ''; }}
                onChange={(event) => setHomeScore(Math.max(0, Number(event.target.value) || 0))}
              />
            </label>
            <strong>—</strong>
            <label>
              <span>{matchInfo?.opponent || t.opponent}</span>
              <input
                type="number"
                min="0"
                max="99"
                value={awayScore}
                onFocus={(event) => { if (event.currentTarget.value === '0') event.currentTarget.value = ''; }}
                onChange={(event) => setAwayScore(Math.max(0, Number(event.target.value) || 0))}
              />
            </label>
          </div>

          <div className="final-detail-grid">
            <label>
              <span>{t.scorers}</span>
              <textarea
                value={scorersText}
                onChange={(event) => setScorersText(event.target.value)}
                placeholder={t.onePlayerPerLine}
                rows={4}
              />
            </label>
            <label>
              <span>{t.assists}</span>
              <textarea
                value={assistsText}
                onChange={(event) => setAssistsText(event.target.value)}
                placeholder={t.onePlayerPerLine}
                rows={4}
              />
            </label>
            <label>
              <span>{t.playerOfTheMatch}</span>
              <select value={playerOfMatchId} onChange={(event) => setPlayerOfMatchId(event.target.value)}>
                <option value="">{t.toBeSelected}</option>
                {players.map((player) => (
                  <option key={player.id} value={player.id}>
                    #{player.squad_number ?? '—'} · {player.name}
                  </option>
                ))}
              </select>
            </label>
          </div>
        </section>
      )}

      <div className="media-studio-editor-grid">
        <section className="media-editor-section">
          <div className="media-editor-heading">
            <div>
              <ImagePlus size={19} />
              <strong>{t.teamPhoto}</strong>
            </div>
            {teamPhoto && (
              <button type="button" className="icon-steel-button" onClick={() => setTeamPhoto('')}>
                <X size={15} />
              </button>
            )}
          </div>

          <label className="media-photo-upload">
            <input
              type="file"
              accept="image/*"
              onChange={(event) => void handleTeamPhoto(event.target.files?.[0])}
            />
            {teamPhoto ? (
              <img src={teamPhoto} alt="" />
            ) : (
              <span>
                <ImagePlus size={28} />
                {t.uploadTeamPhoto}
                <small>{t.teamPhotoHint}</small>
              </span>
            )}
          </label>
        </section>

        <section className="media-editor-section">
          <div className="media-editor-heading">
            <div>
              <UserRoundPlus size={19} />
              <strong>{t.substitutes}</strong>
            </div>
            <span>{selectedSubstitutes.length}</span>
          </div>
          <div className="substitute-selector">
            {benchCandidates.length ? benchCandidates.map((player) => (
              <label key={player.id} className={substituteIds.includes(player.id) ? 'selected' : ''}>
                <input
                  type="checkbox"
                  checked={substituteIds.includes(player.id)}
                  onChange={() => toggleSubstitute(player.id)}
                />
                <span>#{player.squad_number ?? '—'}</span>
                <strong>{player.name}</strong>
                <small>{player.position || '—'}</small>
              </label>
            )) : <p className="muted">{t.noBenchCandidates}</p>}
          </div>
        </section>
      </div>

      <section className="media-editor-section coaches-editor">
        <div className="media-editor-heading">
          <div>
            <UserRoundPlus size={19} />
            <strong>{t.coachingStaff}</strong>
          </div>
          {coaches.length < MAX_COACHES && (
            <button type="button" className="steel-button compact-button" onClick={addCoach}>
              <Plus size={15} />
              {t.addCoach}
            </button>
          )}
        </div>

        <div className="coach-entry-grid">
          {coaches.map((coach, index) => (
            <article className="coach-entry-card" key={coach.id}>
              <div className="coach-entry-fields">
                <input
                  value={coach.role}
                  onChange={(event) => updateCoach(coach.id, 'role', event.target.value)}
                  placeholder={t.coachRole}
                />
                <input
                  value={coach.name}
                  onChange={(event) => updateCoach(coach.id, 'name', event.target.value)}
                  placeholder={t.coachName}
                />
              </div>

              <label className="coach-photo-upload">
                <input
                  type="file"
                  accept="image/*"
                  onChange={(event) => void handleCoachPhoto(coach.id, event.target.files?.[0])}
                />
                {coach.photo ? <img src={coach.photo} alt="" /> : <UserRoundPlus size={21} />}
              </label>

              {coaches.length > 1 && index > 1 && (
                <button
                  type="button"
                  className="icon-steel-button"
                  onClick={() => removeCoach(coach.id)}
                >
                  <Trash2 size={15} />
                </button>
              )}
            </article>
          ))}
        </div>
      </section>

      {mediaMode === 'lineup' && lineupPlayers.length !== 11 && (
        <p className="media-studio-warning">{t.lineupNeedsEleven}</p>
      )}

      <div className="media-studio-actions">
        <button className="gold-button" onClick={() => void share()} disabled={!matchInfo}>
          <Share2 size={17} />
          {t.shareGraphic}
        </button>
        <button className="steel-button" onClick={() => void download()} disabled={!matchInfo}>
          <Download size={17} />
          {t.exportPng}
        </button>
      </div>

      {status && <p className="form-message">{status}</p>}
      <canvas ref={canvasRef} className="matchday-export-canvas" />
    </section>
  );
}
